import type { NavbarMenu } from '@medplum/react';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Brain,
  ClipboardCheck,
  ExternalLink,
  LayoutDashboard,
  LayoutTemplate,
  Sparkles,
  Stethoscope,
} from 'lucide-react';
import {
  panelHealthPractitionerTypes,
  type PanelHealthPractitionerType,
} from '@panel-health/fhir-profiles';

export const appPaths = {
  home: '/',
  signIn: '/signin',
  adminSignIn: '/admin/signin',
  practice: '/practice',
  memories: '/memories',
  templates: '/templates',
  analytics: '/analytics',
  acgmeAssistant: '/acgme-assistant',
  practitionerSpecialty: '/admin/practitioner-specialty',
  subscription: '/subscription',
  terms: '/terms',
  baa: '/baa',
} as const;

interface AppPageDefinition {
  description: string;
  icon: LucideIcon;
  label: string;
  path: string;
  adminOnly?: boolean;
  residentOnly?: boolean;
}

const appPageDefinitions = {
  copilot: {
    path: appPaths.home,
    label: 'Note Pilot',
    description: 'Draft operative notes and coding from a quick case description.',
    icon: Sparkles,
    adminOnly: false,
  },
  practice: {
    path: appPaths.practice,
    label: 'Practice Overview',
    description: 'Review case history, volume, and activity across your practice.',
    icon: LayoutDashboard,
    adminOnly: false,
  },
  memories: {
    path: appPaths.memories,
    label: 'Memories',
    description: 'See and manage what Note Pilot remembers about your preferences.',
    icon: Brain,
    adminOnly: false,
  },
  templates: {
    path: appPaths.templates,
    label: 'Templates',
    description: 'Add and manage note templates for your most common cases.',
    icon: LayoutTemplate,
    adminOnly: false,
  },
  analytics: {
    path: appPaths.analytics,
    label: 'Analytics',
    description: 'Track case trends, volume, and performance patterns.',
    icon: BarChart3,
    adminOnly: false,
  },
  acgmeAssistant: {
    path: appPaths.acgmeAssistant,
    label: 'Case Log Assistant',
    description: 'Review, edit, and confirm case details before approval.',
    icon: ClipboardCheck,
    adminOnly: false,
    residentOnly: true,
  },
  practitionerSpecialty: {
    path: appPaths.practitionerSpecialty,
    label: 'Practitioner Specialty',
    description: 'Assign each practitioner the clinical specialty used for their cases.',
    icon: Stethoscope,
    adminOnly: true,
  },
} as const satisfies Record<string, AppPageDefinition>;

interface AppNavigationSection {
  pageKeys: (keyof typeof appPageDefinitions)[];
  title: string;
}

const appNavigationSections = [
  {
    title: 'Workspace',
    pageKeys: ['copilot', 'practice', 'acgmeAssistant', 'analytics', 'memories', 'templates'],
  },
  {
    title: 'Administration',
    pageKeys: ['practitionerSpecialty'],
  },
] satisfies AppNavigationSection[];

const isPageVisible = (
  page: AppPageDefinition,
  isProjectAdmin: boolean,
  practitionerType: PanelHealthPractitionerType,
) =>
  (isProjectAdmin || !page.adminOnly) &&
  (!page.residentOnly || practitionerType === panelHealthPractitionerTypes.resident);

function toNavbarIcon(Icon: LucideIcon) {
  return <Icon size={18} strokeWidth={2} />;
}

export function buildNavigationMenus(
  isProjectAdmin: boolean,
  practitionerType: PanelHealthPractitionerType,
  mastraStudioHref?: string,
): NavbarMenu[] {
  const menus: NavbarMenu[] = appNavigationSections
    .map((section) => ({
      title: section.title,
      links: section.pageKeys
        .map((pageKey) => appPageDefinitions[pageKey])
        .filter((page) => isPageVisible(page, isProjectAdmin, practitionerType))
        .map((page) => ({
          href: page.path,
          icon: toNavbarIcon(page.icon),
          label: page.label,
        })),
    }))
    .filter((section) => section.links.length > 0);

  if (mastraStudioHref) {
    menus.push({
      title: 'Development',
      links: [
        {
          href: mastraStudioHref,
          icon: toNavbarIcon(ExternalLink),
          label: 'Mastra Studio',
        },
      ],
    });
  }

  return menus;
}

export function buildMastraStudioHref(accessToken: string | undefined): string {
  const studioUrl = new URL(`http://localhost:${import.meta.env.STUDIO_PORT ?? 4111}`);
  if (accessToken) {
    studioUrl.searchParams.set('auth_header', `Bearer ${accessToken}`);
  }
  return studioUrl.href;
}
